import { createSelector } from 'reselect';

import type { MayBe } from 'modules/global/typing/common/common';
import { QUESTION_TYPE } from 'modules/questionModels/constants';
import { getById, getByIds } from 'modules/questionModels/selectors';
import { getQuestionSelfServiceIds } from 'modules/statistics/selectors/ui';
import type { Answer, FormattedAnswer, QuestionSelfServiceData } from 'modules/statistics/types/questions';
import { formatDataSource } from 'utils/formUtils';
import { uniq } from 'utils/ObjectUtils';
import { getProps, getRoot } from 'utils/selectors';

const getQuestionSelfServiceData = (state: {
  dealerStatistics: { data: { selfServiceDetails: { questions: { data: QuestionSelfServiceData } } } };
}) => state.dealerStatistics.data.selfServiceDetails.questions.data;

export const getAnswers = createSelector(
  getQuestionSelfServiceData,
  ({ answers }) => answers
);

export const getQuestionFilters = createSelector(
  getQuestionSelfServiceData,
  ({ filters }) => filters
);

export const hasQuestionFilters = createSelector(
  getQuestionFilters,
  (filters) => Object.values(filters).some((filterChoices) => filterChoices.length > 0)
);

export const hasAnswers = createSelector(
  getAnswers,
  (answers) => Boolean(answers) && Object.values(answers ?? {}).some((questionAnswers) => questionAnswers?.length > 0)
);

export const getIsFetchingQuestionAnswers = createSelector(
  getQuestionSelfServiceData,
  ({ isLoadingAnswers }) => isLoadingAnswers
);

export const getSelectedQuestionModelId = createSelector(
  getQuestionSelfServiceData,
  ({ selectedId }) => selectedId
);

const getSelectedQuestionModel = createSelector(
  getRoot,
  getSelectedQuestionModelId,
  getById
);

const getSelectedQuestionModelQuestions = createSelector(
  getSelectedQuestionModel,
  (questionModel: MayBe<{ questions?: Array<{ id: string; type: string }> }>) =>
    questionModel?.questions?.filter((question) => question.type !== QUESTION_TYPE.INFORMATION) ?? []
);

const getSelectedQuestionModelQuestionIds = createSelector(
  getSelectedQuestionModelQuestions,
  (questions) => questions.map(({ id }) => id)
);

const getAnswerByQuestionId = createSelector(
  getAnswers,
  getProps<string>,
  (answers, questionId) => answers?.[questionId]
);

const getDataAnswerByQuestionId = createSelector(
  getAnswerByQuestionId,
  (answers): Answer[] => answers ?? []
);

const getAnswerByQuestionIds = createSelector(
  getAnswers,
  getProps<string[]>,
  (answers, questionIds): Answer[][] => questionIds.map((id) => answers?.[id] ?? [])
);

export const formatAnswer = (
  answer: Answer,
  activeFilters: string[],
  parentValueByName: Record<string, number>
): FormattedAnswer => ({
  ...answer,
  isFilter: activeFilters.includes(answer.filterKey),
  subsetRatio:
    answer.parent && parentValueByName[answer.parent]
      ? answer.value / parentValueByName[answer.parent]
      : null,
});

export const getFormattedAnswersByQuestionId = createSelector(
  getDataAnswerByQuestionId,
  getQuestionFilters,
  getProps<string>,
  (answers, filters, questionId): FormattedAnswer[] => {
    const activeFilters = filters[questionId] ?? [];

    const parents: Answer[] = [];
    const subsets: Answer[] = [];
    const parentValueByName: Record<string, number> = {};
    for (const answer of answers) {
      if (answer.parent === null) {
        parents.push(answer);
        parentValueByName[answer.name] = answer.value;
      } else {
        subsets.push(answer);
      }
    }

    const subsetsByParent: Record<string, FormattedAnswer[]> = {};
    const orphans: FormattedAnswer[] = [];
    for (const subset of subsets) {
      const formatted = formatAnswer(subset, activeFilters, parentValueByName);
      if (subset.parent && subset.parent in parentValueByName) {
        (subsetsByParent[subset.parent] ??= []).push(formatted);
      } else {
        orphans.push(formatted);
      }
    }

    return [
      ...parents.flatMap((parent) => [
        formatAnswer(parent, activeFilters, parentValueByName),
        ...(subsetsByParent[parent.name] ?? []),
      ]),
      ...orphans,
    ];
  }
);

const getSelectedQuestionModelAnswers = createSelector(
  getRoot,
  getSelectedQuestionModelQuestionIds,
  getAnswerByQuestionIds
);

const getSelectedQuestionModelQuestionsWithAnswers = createSelector(
  getSelectedQuestionModelQuestions,
  getSelectedQuestionModelAnswers,
  (questions, answers) =>
    questions.reduce<Array<typeof questions[number] & { answers: Answer[] }>>((acc, question, index) => {
      if (answers[index]) {
        acc.push({
          ...question,
          answers: answers[index],
        });
      }
      return acc;
    }, [])
);

export const getSelectQuestionModelQuestionById = createSelector(
  getSelectedQuestionModelQuestions,
  getProps<string>,
  (questions, questionId) => questions.find(({ id }) => id === questionId)
);

export const getSelectedQuestionModelQuestionsIds = createSelector(
  getSelectedQuestionModelQuestionsWithAnswers,
  (questionList) => questionList.map((question) => question.id)
);

export const getFiltersRequest = createSelector(
  getQuestionFilters,
  hasQuestionFilters,
  (filters, hasFilters): MayBe<string[]> =>
    (hasFilters
      ? Object.entries(filters)
        .filter(([, answers]) => answers.length > 0)
        .map(([questionId, answers]) => `${questionId},${answers.join(',')}`)
      : undefined)
);

const getQuestionSelfServiceListIds = createSelector(
  getQuestionSelfServiceIds,
  getSelectedQuestionModelId,
  (idsFromSelect: string[], selectedId): string[] => uniq([selectedId, ...idsFromSelect])
);

const getQuestionSelfServiceList = createSelector(
  getRoot,
  getQuestionSelfServiceListIds,
  getByIds
);

export const getFormattedQuestionSelfServiceList = createSelector(
  getQuestionSelfServiceList,
  getProps<string>,
  (questions, defaultName) => formatDataSource(questions, defaultName)
);
